// ═══════════════════════════════════════════════════════════════
// LegalHoldConflictView.jsx — Forensic conflict viewer for support
// when responding to a subpoena. Loads the conflict by ID, restores
// the deleted user's original identity (from the supplied legal-hold
// record), and displays a read-only summary suitable for exporting
// into legal discovery materials.
//
// Only support role can reach this — gated by LegalHoldLookupPanel
// which requires a server-validated lookup before rendering this.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';
import { storage } from '../shared.jsx';

function LegalHoldConflictView({ conflictId, legalHold, onBack }) {
  const [conflict, setConflict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const c = await storage.get(`conflict:${conflictId}`, true);
        if (!c) {
          setError('Conflict not found. It may have been purged after the 7-year retention period.');
        } else {
          setConflict(c);
        }
      } catch (e) {
        setError(e.message || 'Failed to load conflict.');
      } finally {
        setLoading(false);
      }
    })();
  }, [conflictId]);

  // Restore original identity wherever the user appears as "Deleted User"
  const restoreIdentity = (rawName, userId) => {
    if (userId === legalHold.originalUserId) {
      return `${legalHold.originalName || '(no name on record)'} <${legalHold.originalEmail || '(no email)'}>`;
    }
    return rawName || `(user ${userId})`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 p-8 flex items-center justify-center text-stone-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading conflict…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <button onClick={onBack} className="text-xs text-amber-700 hover:underline inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3 h-3" /> Back to lookup
        </button>
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  const auditLog = Array.isArray(conflict.auditLog) ? conflict.auditLog : [];
  const mentees = Array.isArray(conflict.mentees) ? conflict.mentees : [];

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-xs text-amber-700 hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to lookup
        </button>
        <span className="text-[11px] uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full font-medium">
          Forensic view: identity restored
        </span>
      </div>

      <header className="border-b border-stone-200 pb-3">
        <h1 className="text-xl font-semibold text-stone-800">{conflict.title || '(untitled conflict)'}</h1>
        <div className="grid grid-cols-2 gap-2 text-xs text-stone-600 mt-2">
          <div><span className="text-stone-500">Status:</span> {conflict.status || 'in-progress'}</div>
          <div><span className="text-stone-500">Conflict ID:</span> <span className="font-mono">{conflict.id || conflictId}</span></div>
          <div><span className="text-stone-500">Created:</span> {conflict.createdAt || '—'}</div>
          <div><span className="text-stone-500">Tenant:</span> <span className="font-mono">{conflict.tenantId || 'tenant-public'}</span></div>
          {conflict.resolvedAt && (
            <div className="col-span-2"><span className="text-stone-500">Resolved:</span> {conflict.resolvedAt}</div>
          )}
        </div>
      </header>

      <section>
        <h2 className="text-sm font-semibold text-stone-700 mb-2">Identity reference</h2>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 space-y-1">
          <div><span className="text-amber-700">Original name:</span> <span className="font-medium">{legalHold.originalName || '—'}</span></div>
          <div><span className="text-amber-700">Original email:</span> <span className="font-medium">{legalHold.originalEmail || '—'}</span></div>
          <div><span className="text-amber-700">Original user ID:</span> <span className="font-mono">{legalHold.originalUserId}</span></div>
          <div><span className="text-amber-700">Deleted at:</span> {legalHold.deletedAt}</div>
          <div><span className="text-amber-700">Legal basis:</span> {legalHold.legalBasis}</div>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-stone-700 mb-2">Participants</h2>
        <ul className="space-y-1 text-sm">
          {conflict.createdBy && (
            <li className="text-stone-700">
              <span className="text-stone-500 text-xs">Creator:</span> {restoreIdentity(null, conflict.createdBy)}
            </li>
          )}
          {mentees.map((m) => (
            <li key={m.id} className="text-stone-700">
              <span className="text-stone-500 text-xs">Participant:</span> {restoreIdentity(m.name, m.id)}
              {m.email && m.id !== legalHold.originalUserId && ` <${m.email}>`}
            </li>
          ))}
          {conflict.flyOnWall?.id && (
            <li className="text-stone-700">
              <span className="text-stone-500 text-xs">Observer:</span> {restoreIdentity(conflict.flyOnWall.name, conflict.flyOnWall.id)}
            </li>
          )}
          {conflict.omniscient?.id && (
            <li className="text-stone-700">
              <span className="text-stone-500 text-xs">Global:</span> {restoreIdentity(conflict.omniscient.name, conflict.omniscient.id)}
            </li>
          )}
        </ul>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-stone-700 mb-2">Step content (read-only JSON dump)</h2>
        <pre className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-[11px] font-mono text-stone-700 overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap">
{JSON.stringify({
  identifyDefine: conflict.identifyDefine,
  communicate: conflict.communicate,
  exploreAlternatives: conflict.exploreAlternatives,
  evaluateSelect: conflict.evaluateSelect,
  agreeImplement: conflict.agreeImplement,
  followUp: conflict.followUp,
}, null, 2)}
        </pre>
        <p className="text-[11px] text-stone-500 mt-1">
          Copy this JSON block (with the Identity reference above) for inclusion in discovery materials.
        </p>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-stone-700 mb-2">Audit log ({auditLog.length} entries)</h2>
        {auditLog.length === 0 ? (
          <p className="text-xs text-stone-500">No audit entries.</p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {auditLog.map((entry, idx) => (
              <div key={idx} className="text-[11px] text-stone-700 bg-stone-50 border border-stone-200 rounded p-2">
                <div className="flex items-center justify-between text-stone-500">
                  <span>{entry.at || entry.timestamp || '—'}</span>
                  <span>{restoreIdentity(entry.userName, entry.userId)}</span>
                </div>
                <div className="mt-0.5 font-medium text-stone-800">{entry.action || entry.event || '(no action)'}</div>
                {entry.details && <div className="mt-0.5 text-stone-600 whitespace-pre-wrap">{entry.details}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="border-t border-stone-200 pt-3 text-[11px] text-stone-500 flex items-center gap-2">
        <FileText className="w-3 h-3" />
        Lookup audit-logged on the server. Conflict ID and legal-hold ID above are sufficient to retrieve the raw record from Firebase if needed.
      </div>
    </div>
  );
}

export default LegalHoldConflictView;
